import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from '../theme/ThemeContext';
import { usePermission } from '../context/PermissionContext';
import useChargeItemStore from '../stores/chargeItemStore';
import { CHARGE_ITEM_GST_RATES } from '../services/api/chargeItemsApi';
import { formatRupees } from '../utils/money';
import AdminPageHeader from '../components/common/AdminPageHeader';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import { ConfirmationModal } from '../components/common';
import './ProcedureCatalogPage.css';

// Day-1 IPD / S1 — Charge Items admin catalog. Clone of the B2 admin-catalog
// pattern (org-global, no dept machinery — dept-tag/category-grouping/usage
// totals are DEFERRED, named in the story, not silently dropped). Unlike the
// other B2 catalogs, a non-admin who reaches this screen still sees the page
// shell (not a Navigate bounce) with the exact AC-NEG copy — the admin
// controls (add/edit/deactivate/reactivate) are simply absent.
//
// AC-LONGLIST: results render bounded (PAGE_SIZE at a time, "Show more" to
// reveal further matches — no unbounded flat render for 100+ items) and the
// Add/Edit form always anchors ABOVE the table (never inline per-row like
// SurgeryCatalogPage's Variant-A), scrolling into view on open so it's never
// left off-screen below a long, scrolled list.

const PAGE_SIZE = 50;

const blankRow = () => ({
  name: '',
  price: '',
  gst_rate: 0,
  category: '',
  variable_price: false,
  min_price: '',
  max_price: '',
});

// AC8 — the hard `price > 0` require is RELAXED for variable items (blank
// suggested price is legal); still enforced for fixed items (byte-unchanged).
// AC5/AC6 — merged-row-style client hints for the range: min>=max lands on
// the max field, an out-of-range suggested price lands on the price field —
// same field keys the server's 422 codes map onto (INVALID_PRICE_RANGE /
// SUGGESTED_PRICE_OUT_OF_RANGE), so client + server errors render in the
// same place. Server is still the real fence (AC7) — this is convenience only.
function validate(row) {
  const e = {};
  const name = row.name.trim();
  if (!name) e.name = 'Required';
  else if (name.length > 120) e.name = 'Must be 120 characters or fewer';

  const variable = !!row.variable_price;
  const priceRaw = row.price;
  const price = Number(priceRaw);

  if (!variable) {
    if (priceRaw === '' || Number.isNaN(price) || price <= 0) {
      e.price = 'Enter a price above ₹0';
    }
  }
  if (row.category && row.category.trim().length > 60) {
    e.category = 'Must be 60 characters or fewer';
  }

  if (variable) {
    const minRaw = row.min_price;
    const maxRaw = row.max_price;
    // A typed 0 minimum means "no minimum" (mirrors the server normalising
    // min_price=0 → null) — never treated as a real floor.
    const min = minRaw === '' || minRaw == null || Number(minRaw) === 0 ? null : Number(minRaw);
    const max = maxRaw === '' || maxRaw == null ? null : Number(maxRaw);

    if (min != null && max != null && min >= max) {
      e.max_price = 'Maximum must be more than the minimum.';
    }
    if (priceRaw !== '' && !Number.isNaN(price)) {
      const belowMin = min != null && price < min;
      const aboveMax = max != null && price > max;
      if (belowMin || aboveMax) {
        e.price = 'Suggested price must be within the minimum and maximum.';
      }
    }
  }
  return e;
}

// AC9 — list Price column render rules; single source of truth for both the
// table cell and (indirectly) any future reader of this shape. Uses the ONE
// rupee formatter (`formatRupees`) for both the fixed cell and every Varies
// variant, so grouping/decimals never diverge between them. A stored 0
// minimum (legacy pre-normalisation row) reads as "no minimum", same as the
// admin form.
function priceCell(item) {
  if (!item.variable_price) return formatRupees(item.price || 0);
  const min_price = item.min_price != null && Number(item.min_price) > 0 ? item.min_price : null;
  const { max_price } = item;
  if (min_price != null && max_price != null) return `Varies · ${formatRupees(min_price)}–${formatRupees(max_price)}`;
  if (min_price != null) return `Varies · ${formatRupees(min_price)} or more`;
  if (max_price != null) return `Varies · ${formatRupees(max_price)} or less`;
  return 'Varies';
}

export default function ChargeItemsPage() {
  const { isDark } = useTheme();
  const canManage = usePermission('MANAGE_CHARGE_MASTER');

  const {
    items,
    isLoading,
    error,
    includeInactive,
    fetchChargeItems,
    createChargeItem,
    updateChargeItem,
    deactivateChargeItem,
    reactivateChargeItem,
    setIncludeInactive,
    clearError,
  } = useChargeItemStore();

  const [form, setForm] = useState(null); // null | {mode:'add'} | {mode:'edit', id}
  const [row, setRow] = useState(blankRow());
  const [orig, setOrig] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [banner, setBanner] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const formRef = useRef(null);

  // Initial load — stable deps, single-flight store (bounded requests).
  useEffect(() => {
    fetchChargeItems();
  }, [fetchChargeItems]);

  // A long list + a live search must not leave stale pagination behind.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery]);

  // Add/Edit form always anchors above the table — scroll it into view so a
  // desk user scrolled deep into a 100+ item list still sees it open.
  useEffect(() => {
    if (form && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [form]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.category || '').toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = filteredItems.length > visibleItems.length;

  const change = (k, v) => setRow((r) => ({ ...r, [k]: v }));

  const openAdd = () => {
    setRow(blankRow());
    setOrig(null);
    setErrors({});
    setBanner(null);
    setForm({ mode: 'add' });
  };

  const openEdit = (item) => {
    // Seed ONLY form fields — read-only/derived keys (item_id, active,
    // timestamps) never enter form state. `price` on a variable item is the
    // "suggested price" — D3: a stored 0 means "not configured", so it opens
    // blank rather than showing a meaningless ₹0.
    const variable = !!item.variable_price;
    setRow({
      name: item.name || '',
      price: variable ? (item.price > 0 ? item.price : '') : (item.price ?? ''),
      gst_rate: item.gst_rate ?? 0,
      category: item.category || '',
      variable_price: variable,
      // A stored 0 (legacy pre-normalisation row) means "no minimum" — opens
      // blank, same as null.
      min_price: item.min_price != null && Number(item.min_price) > 0 ? item.min_price : '',
      max_price: item.max_price ?? '',
    });
    setOrig(item);
    setErrors({});
    setBanner(null);
    setForm({ mode: 'edit', id: item.item_id });
  };

  const close = () => {
    setForm(null);
    setOrig(null);
    setErrors({});
  };

  // Turning OFF variable pricing on an item that already has a min/max needs
  // a confirm first (screen interactions) — the checkbox stays controlled by
  // `row.variable_price`, so it visually "reverts" until the user confirms.
  const onToggleVariable = (checked) => {
    if (!checked) {
      const hadRange = orig && (orig.min_price != null || orig.max_price != null);
      if (hadRange) {
        setConfirm({
          title: 'Turn off variable pricing?',
          message: 'The minimum and maximum will be removed. Charges already posted keep their amounts.',
          ctaLabel: 'Turn off',
          onConfirm: () => {
            setConfirm(null);
            setRow((r) => ({ ...r, variable_price: false, min_price: '', max_price: '' }));
          },
        });
        return;
      }
      setRow((r) => ({ ...r, variable_price: false, min_price: '', max_price: '' }));
      return;
    }
    // Toggling ON an existing FIXED item carries its current price into
    // Suggested price automatically — `row.price` already holds it from
    // openEdit, and this handler never clears it.
    setRow((r) => ({ ...r, variable_price: true }));
  };

  const clientErrs = validate(row);
  const saveDisabled = Object.keys(clientErrs).length > 0 || busy;
  const saveDisabledReason = row.variable_price
    ? clientErrs.max_price || clientErrs.price || clientErrs.name || 'Enter a name to save.'
    : 'Enter a name and a price above ₹0 to save.';

  // Server 422s can land on the same fields the client already validates
  // (AC7 — merged-row check can reject what the client's local snapshot
  // thought was fine, e.g. a concurrent edit). Client validation wins when
  // it also flags the field; otherwise the server's message shows through.
  const fieldError = (key) => {
    if (key === 'max_price') return errors.max_price || (row.variable_price ? clientErrs.max_price : undefined);
    if (key === 'price' && row.variable_price) return errors.price || clientErrs.price;
    return errors[key];
  };

  const submit = async () => {
    const e = validate(row);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);

    let result;
    if (form.mode === 'add') {
      const body = {
        name: row.name.trim(),
        gst_rate: Number(row.gst_rate),
      };
      if (row.variable_price) {
        // Fixed-item POST body stays byte-unchanged (else branch below).
        // Variable: `price` is the SUGGESTED price — omitted when blank, D3
        // normalises that to 0.0 server-side. min/max are one-sided-optional.
        body.variable_price = true;
        if (row.price !== '') body.price = Number(row.price);
        // A typed 0 minimum means "no minimum" — omit rather than send 0.
        if (row.min_price !== '' && Number(row.min_price) !== 0) body.min_price = Number(row.min_price);
        if (row.max_price !== '') body.max_price = Number(row.max_price);
      } else {
        body.price = Number(row.price);
      }
      if (row.category.trim()) body.category = row.category.trim();
      result = await createChargeItem(body);
    } else {
      // PATCH = allowlist DIFF vs the original row (read-only keys can't leak).
      const patch = {};
      const changed = (key, newVal, origVal) => {
        if (newVal !== origVal) patch[key] = newVal;
      };
      changed('name', row.name.trim(), orig.name);
      changed('gst_rate', Number(row.gst_rate), Number(orig.gst_rate ?? 0));
      changed('category', row.category.trim() || null, orig.category || null);

      const rowVariable = !!row.variable_price;
      const origVariable = !!orig.variable_price;
      changed('variable_price', rowVariable, origVariable);

      // `price` = suggested price when variable (blank ⇒ explicit 0, D3),
      // required amount when fixed — same diff mechanics either way.
      const newPrice = rowVariable ? (row.price === '' ? 0 : Number(row.price)) : Number(row.price);
      changed('price', newPrice, Number(orig.price || 0));

      // D6 — explicit null CLEARS a bound; only sent when it actually
      // differs from what's stored (toggling off, narrowing to unbounded).
      // A typed 0 minimum means "no minimum" — treated the same as blank.
      const newMin = rowVariable && row.min_price !== '' && Number(row.min_price) !== 0 ? Number(row.min_price) : null;
      const newMax = rowVariable && row.max_price !== '' ? Number(row.max_price) : null;
      changed('min_price', newMin, orig.min_price ?? null);
      changed('max_price', newMax, orig.max_price ?? null);

      if (Object.keys(patch).length === 0) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'No changes to save' });
        return;
      }
      result = await updateChargeItem(form.id, patch);
    }

    setBusy(false);
    if (!result.success) {
      // Map the server's machine-readable code (§1.4) to the field it
      // belongs to. RANGE_ON_FIXED_ITEM has no single owning field on this
      // form (it means the merged row disagrees about the toggle itself) —
      // falls through to the banner.
      const codeFieldMap = {
        INVALID_GST_RATE: 'gst_rate',
        PRICE_REQUIRED: 'price',
        INVALID_PRICE_RANGE: 'max_price',
        SUGGESTED_PRICE_OUT_OF_RANGE: 'price',
      };
      const field = codeFieldMap[result.code];
      if (field) {
        setErrors((prev) => ({ ...prev, [field]: result.error }));
      } else {
        setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      }
      return;
    }
    const isAdd = form.mode === 'add';
    close();
    setBanner({ kind: 'ok', msg: isAdd ? 'Charge item created' : 'Charge item updated' });
  };

  const askDeactivate = (item) => {
    setConfirm({
      title: 'Deactivate charge item?',
      message: `Hide '${item.name}' from the billing desk? Charges already posted from it stay on their bills. You can reactivate it any time.`,
      ctaLabel: 'Deactivate',
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateChargeItem(item.item_id);
        setBanner(
          r.success
            ? { kind: 'ok', msg: 'Charge item deactivated' }
            : { kind: 'err', msg: r.error || 'Deactivate failed' }
        );
      },
    });
  };

  const reactivate = async (item) => {
    const r = await reactivateChargeItem(item.item_id);
    setBanner(
      r.success
        ? { kind: 'ok', msg: 'Charge item reactivated' }
        : { kind: 'err', msg: r.error || 'Reactivate failed' }
    );
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const inputCls =
    'w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const labelCls =
    'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  // AC-NEG — non-admin still sees the page shell (never a bare redirect),
  // just without any of the management controls.
  if (!canManage) {
    return (
      <div className="pcp-page" style={{ background: bg }}>
        <AdminPageHeader />
        <main className="pcp-main">
          <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
            Charge items
          </h1>
          <div className="pcp-state">Only an admin can manage charge items.</div>
        </main>
      </div>
    );
  }

  const formPanel = form && (
    <div className="pcp-add-wrap" ref={formRef}>
      <div className="pcp-add-title">
        {form.mode === 'add' ? 'New charge item' : `Edit · ${orig?.name || ''}`}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-2">
          <label className={labelCls}>Name *</label>
          <input
            className={inputCls}
            aria-label="Name *"
            value={row.name}
            onChange={(e) => change('name', e.target.value)}
            placeholder="Labour room"
          />
          {errors.name && <div className="text-xs text-rose-600 mt-1">{errors.name}</div>}
        </div>

        <div>
          <label className={labelCls}>
            {row.variable_price ? 'Suggested price (₹)' : 'Price (₹) *'}
          </label>
          <input
            className={inputCls}
            type="number"
            min="0"
            step="0.01"
            aria-label={row.variable_price ? 'Suggested price (₹)' : 'Price (₹) *'}
            value={row.price}
            onChange={(e) => change('price', e.target.value)}
          />
          {fieldError('price') && <div className="text-xs text-rose-600 mt-1">{fieldError('price')}</div>}
        </div>

        <div>
          <label className={labelCls}>GST</label>
          <select
            className={inputCls}
            aria-label="GST"
            value={row.gst_rate}
            onChange={(e) => change('gst_rate', e.target.value)}
          >
            {CHARGE_ITEM_GST_RATES.map((g) => (
              <option key={g} value={g}>{g === 0 ? 'None' : `${g}%`}</option>
            ))}
          </select>
          {errors.gst_rate && <div className="text-xs text-rose-600 mt-1">{errors.gst_rate}</div>}
        </div>

        <div className="lg:col-span-2">
          <label className={labelCls}>Category (optional)</label>
          <input
            className={inputCls}
            aria-label="Category"
            value={row.category}
            onChange={(e) => change('category', e.target.value)}
            placeholder="Room charges"
          />
          {errors.category && <div className="text-xs text-rose-600 mt-1">{errors.category}</div>}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg p-3 mt-3 border bg-teal-50 dark:bg-teal-900/40 border-teal-300 dark:border-teal-700">
        <input
          type="checkbox"
          aria-label="Price varies"
          checked={!!row.variable_price}
          onChange={(e) => onToggleVariable(e.target.checked)}
          className="mt-1"
        />
        <div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            Price varies (the billing desk types the amount)
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
            Off: one fixed price, used every time. On: the billing desk types the amount when
            billing — set a minimum and maximum below to keep it in range.
          </div>
        </div>
      </div>

      {row.variable_price && (
        <div className="mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Minimum (₹)</label>
              <input
                className={inputCls}
                type="number"
                min="0"
                step="0.01"
                aria-label="Minimum (₹)"
                value={row.min_price}
                onChange={(e) => change('min_price', e.target.value)}
                placeholder="e.g. 200"
              />
            </div>
            <div>
              <label className={labelCls}>Maximum (₹)</label>
              <input
                className={inputCls}
                type="number"
                min="0"
                step="0.01"
                aria-label="Maximum (₹)"
                value={row.max_price}
                onChange={(e) => change('max_price', e.target.value)}
                placeholder="e.g. 500"
              />
              {fieldError('max_price') && (
                <div className="text-xs text-rose-600 mt-1">{fieldError('max_price')}</div>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Both are optional. Set only a minimum, only a maximum, or neither — with neither, the
            billing desk can enter any amount above ₹0.
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button
          className="pcp-btn pcp-btn-primary"
          onClick={submit}
          disabled={saveDisabled}
          title={saveDisabled && !busy ? saveDisabledReason : undefined}
        >
          {busy ? 'Saving…' : form.mode === 'add' ? 'Add charge item' : 'Save changes'}
        </button>
        <button className="pcp-btn pcp-btn-secondary" onClick={close} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              Charge items
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              The items your organisation bills. Set one fixed price, or let the billing desk type
              the amount within limits you set.
            </p>
          </div>
          <button className="pcp-btn pcp-btn-primary" onClick={openAdd} disabled={!!form}>
            + Add charge item
          </button>
        </div>
        <FeesBillingTabs active="charge-items" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        <div className="pcp-filter-bar">
          <input
            type="text"
            className="pcp-search"
            placeholder="Search charge items…"
            aria-label="Search charge items…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          <span className="pcp-count">
            {filteredItems.length} charge item{filteredItems.length === 1 ? '' : 's'}
          </span>
        </div>

        {form && formPanel}

        {isLoading ? (
          <div className="pcp-state">Loading charge items…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => { clearError(); fetchChargeItems({ force: true }); }}
            >
              Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="pcp-state">
            No charge items yet. Add the items your organisation bills so the billing desk can
            pick them.
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="pcp-state">No charge items match your search.</div>
        ) : (
          <>
            <table className="pcp-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price ₹</th>
                  <th>GST</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((p) => (
                  <tr key={p.item_id} className={p.active === false ? 'pcp-row pcp-row-inactive' : 'pcp-row'}>
                    <td>
                      <div className="pcp-name">{p.name}</div>
                      <div className="pcp-subname">
                        {p.item_id}
                        {p.category && ` · ${p.category}`}
                      </div>
                    </td>
                    <td className="pcp-cost">
                      <span className={p.variable_price ? 'text-amber-600 dark:text-amber-400 font-semibold' : undefined}>
                        {priceCell(p)}
                      </span>
                    </td>
                    <td>{p.gst_rate ? `${p.gst_rate}%` : 'None'}</td>
                    <td>
                      <span className={p.active === false ? 'pcp-chip pcp-chip-inactive' : 'pcp-chip'}>
                        {p.active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="pcp-actions">
                        <button className="pcp-btn pcp-btn-link" onClick={() => openEdit(p)} disabled={!!form}>
                          Edit
                        </button>
                        {p.active !== false ? (
                          <button
                            className="pcp-btn pcp-btn-link pcp-btn-danger-link"
                            onClick={() => askDeactivate(p)}
                            disabled={!!form}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button className="pcp-btn pcp-btn-link" onClick={() => reactivate(p)} disabled={!!form}>
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div className="pcp-filter-bar">
                <span className="pcp-count">
                  Showing {visibleItems.length} of {filteredItems.length} — keep typing to narrow
                </span>
                <button
                  className="pcp-btn pcp-btn-secondary"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                >
                  Show more
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {confirm && (
        <ConfirmationModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          ctaLabel={confirm.ctaLabel}
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
